/**
 * Monster Onesie — header pin (BRIEF §3.2).
 *
 * Only runs when the header is in overlay mode (the `transparent` attribute,
 * set by header.liquid on the homepage). It watches the overlay anchor — the
 * hero card, marked with [data-monster-overlay-anchor] — and flips the header
 * to its pinned state once the anchor's bottom edge passes 70px from the top
 * of the viewport, matching the mockup's `hc.getBoundingClientRect().bottom < 70`.
 *
 * Why not Horizon's own sticky observer: header.js watches the *header's* own
 * box, so it flips almost immediately on scroll. §3.2 wants the header to ride
 * down with the hero and only pin at the hero's bottom edge. Ritual's
 * enable_sticky_header is therefore set to "never" — Horizon writes no
 * data-sticky-state at all — and this component owns the state exclusively.
 * Sticky behaviour on non-overlay templates is plain CSS (see header.liquid).
 *
 * If there is no anchor on the page, nothing happens and the header keeps
 * whatever state the CSS gives it. That is the correct degradation for a
 * homepage built without a hero.
 */

const PIN_THRESHOLD = 70;

class MonsterHeaderPin extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLElement | null} */
  #header = null;

  connectedCallback() {
    this.#header = this.closest('header-component');

    // Not in overlay mode: the header is a normal sticky bar and needs no JS.
    if (!this.#header || !this.#header.hasAttribute('transparent')) return;

    const anchor = document.querySelector('[data-monster-overlay-anchor]');
    if (!anchor) return;

    // Set the state synchronously before observing. IntersectionObserver's
    // first callback is async, so on a reload part-way down the page there
    // would otherwise be a frame where the header paints unpinned over
    // whatever happens to be under it. Measuring once here makes first paint
    // match the settled state.
    this.#setPinned(anchor.getBoundingClientRect().bottom < PIN_THRESHOLD);

    // A top margin of -70px moves the observer's top edge down to exactly the
    // pin threshold, so the callback fires on the crossing we care about
    // rather than on every scroll frame.
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#setPinned(entry.boundingClientRect.bottom < PIN_THRESHOLD);
      },
      { rootMargin: `-${PIN_THRESHOLD}px 0px 0px 0px`, threshold: 0 }
    );

    this.#observer.observe(anchor);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {boolean} pinned */
  #setPinned(pinned) {
    if (!this.#header) return;

    if (pinned) {
      this.#header.dataset.monsterPin = 'on';
    } else {
      delete this.#header.dataset.monsterPin;
    }
  }
}

if (!customElements.get('monster-header-pin')) {
  customElements.define('monster-header-pin', MonsterHeaderPin);
}
